import React from "react";
import { Link } from "react-router-dom";
import { useCatalog } from "../contexts/CatalogContext";
import ServicePrice from "../components/ServicePrice";
import FaqSection from "../components/home/FaqSection";
import { SERVICE_PAGES, hasCatalogServices } from "../seo/pages";

const heading = "font-display font-bold text-2xl sm:text-3xl text-slate-900";
const linkStyle = "font-semibold text-[#0369A1] underline underline-offset-4 hover:text-[#075985]";

export default function ServiceLanding({ page }) {
  const { categories, loading, error } = useCatalog();
  const category = categories.find(item => item.id === page.categoryId);
  const hasServices = hasCatalogServices(category);
  const bookable = hasServices && category.status !== "soon" && !loading && !error;
  const related = SERVICE_PAGES.filter(item => item !== page && hasCatalogServices(categories.find(cat => cat.id === item.categoryId)));
  const faqs = [...page.faq, {
    q: "How can I check the price and book in Lucknow?",
    a: "Review the current catalog options and their inclusions on this page. When booking is available, select a service, provide your appliance and address details, choose a slot and review the booking. For unavailable options or questions about coverage, contact Repairro first.",
  }];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <nav aria-label="Breadcrumb" className="text-sm text-slate-600 mb-8">
        <Link to="/" className={linkStyle}>Repairro home</Link> / <Link to="/services" className={linkStyle}>Services</Link> / <span aria-current="page">{page.h1}</span>
      </nav>
      <header className="max-w-3xl">
        <p className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Repairro · Lucknow</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 mt-3">{page.h1}</h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">{page.intro}</p>
        {bookable ? (
          <Link to={`/booking?category=${page.categoryId}`} className="inline-flex mt-6 bg-[#0369A1] hover:bg-[#075985] text-white font-semibold px-7 py-3 rounded-xl">Book {category.short} service</Link>
        ) : (
          <p className="mt-6 rounded-xl bg-sky-50 p-4 text-slate-700" role="status">
            {loading ? "Checking current service availability… " : error ? "Current catalog availability could not be confirmed. " : category?.status === "soon" ? "Online bookings for this category are coming soon. " : "This category is not currently available in the catalog. "}
            <Link to="/contact" className={linkStyle}>Contact Repairro about availability</Link>.
          </p>
        )}
      </header>

      <section className="mt-14" aria-labelledby="common-problems">
        <h2 id="common-problems" className={heading}>Common {page.categoryId === "washing_machine" ? "washing machine" : page.categoryId.toUpperCase()} problems and service needs</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-6">
          {page.problems.map(([title, text]) => (
            <article key={title} className="bg-white border border-slate-200 rounded-2xl p-6 card-shadow">
              <h3 className="font-display font-bold text-lg text-slate-900">{title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{text}</p>
            </article>
          ))}
        </div>
      </section>

      {hasServices && !loading && !error && (
        <section className="mt-14" aria-labelledby="service-options">
          <h2 id="service-options" className={heading}>{category.short} repair, installation and care options</h2>
          <p className="mt-3 text-slate-600">Compare the scope and prices currently listed in the Repairro catalog. Check each option for included work and any parts exclusions.{!bookable && " These are catalog options; online booking is not currently available for this category."}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {category.services.map(service => (
              <article key={service.id} className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col card-shadow">
                <h3 className="font-display font-bold text-lg text-slate-900">{service.name}</h3>
                <p className="mt-3 text-slate-600">{service.desc}</p>
                {service.price != null && <p className="mt-4 text-[#0369A1]"><ServicePrice service={service} /></p>}
                <ul className="list-disc pl-5 mt-4 space-y-2 text-sm text-slate-600 flex-1">
                  {(service.includes || []).map(item => <li key={item}>{item}</li>)}
                </ul>
                {bookable && <Link to={`/booking?category=${encodeURIComponent(category.id)}&service=${encodeURIComponent(service.id)}`} className={`mt-5 ${linkStyle}`}>Book {service.name}</Link>}
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="mt-14 grid md:grid-cols-2 gap-8">
        <div>
          <h2 className={heading}>Prepare for your technician visit</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">{page.preparation}</p>
          <p className="mt-4 text-slate-600 leading-relaxed">Use the listed service scope to discuss the work required. Ask about any replacement parts and additional work before proceeding. Keep your booking reference for follow-up with Repairro.</p>
        </div>
        <div>
          <h2 className={heading}>Service enquiries in Lucknow</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">Lucknow is a live service city in Repairro's coverage list. Availability depends on the category and appointment. Provide your complete address during booking, or contact the team to check your location and service needs first.</p>
          <p className="mt-4"><Link to="/contact" className={linkStyle}>Ask about Lucknow service coverage</Link></p>
        </div>
      </section>

      {!!category?.brands?.length && !loading && !error && (
        <section className="mt-14 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className={heading}>{category.short} brands in our catalog</h2>
          <p className="mt-3 text-slate-600">{category.brands.join(", ")}. Share your model when enquiring so the team can check the service required.</p>
        </section>
      )}
      <FaqSection faqs={faqs} />
      <nav aria-label="More service information" className="border-t border-slate-200 pt-8 flex flex-wrap gap-x-6 gap-y-4">
        <Link to="/" className={linkStyle}>Repairro home</Link>
        <Link to="/services" className={linkStyle}>Full service catalog</Link>
        <Link to="/pricing" className={linkStyle}>Service pricing</Link>
        <Link to="/contact" className={linkStyle}>Contact Repairro</Link>
        <Link to="/#customer-reviews" className={linkStyle}>Customer reviews</Link>
        {related.map(item => <Link key={item.path} to={item.path} className={linkStyle}>{item.label}</Link>)}
      </nav>
    </div>
  );
}
